/**
 * OE / ordinary sale amounts per party from Firestore `sales_import_months` / lineChunks.
 */
import { lineAmountOnly } from "../features/salesDataImport/services/salesLineChunksReader";
import {
  MONTHLY_AUTOS_BRANDS,
  normalizeBrand,
  oeBrandFromBrandField,
} from "./monthlyAutosReportService";
import {
  appBranchFromImportRow,
  importRowBrandNameField,
  includeRowInMonthlyImportReport,
  isImportSaleVoucherRow,
  loadSalesImportRows,
} from "./salesImportTransactionDataService";

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthWindow(year, monthIndex) {
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export function normalizePartyName(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Customer / party on a sales_import_months line row. */
export function importRowPartyName(row) {
  return String(row?.customerName ?? row?.customer ?? row?.party ?? row?.partyName ?? "").trim();
}

/** Salesperson on a sales_import_months line row (staff name preferred). */
export function importRowEmployeeName(row) {
  const staff = String(row?.staffName ?? row?.StaffName ?? row?.nameStaffName ?? "").trim();
  if (staff) return staff;
  return String(
    row?.employeeName ?? row?.EmployeeName ?? row?.Employee ?? row?.employee ?? ""
  ).trim();
}

export function normalizeEmployeeName(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Match import row employee to any selected name (exact, case-insensitive). */
export function rowMatchesSelectedEmployees(row, employeeNames) {
  const rowEmp = normalizeEmployeeName(importRowEmployeeName(row));
  if (!rowEmp) return false;
  for (const n of employeeNames || []) {
    const sel = normalizeEmployeeName(typeof n === "string" ? n : n?.name ?? "");
    if (!sel) continue;
    if (rowEmp === sel) return true;
  }
  return false;
}

/** Match import row party to any selected party (exact, case-insensitive). */
export function rowMatchesSelectedParties(row, selectedParties) {
  const rowParty = normalizePartyName(importRowPartyName(row));
  if (!rowParty) return false;
  for (const p of selectedParties || []) {
    const name = typeof p === "string" ? p : p?.partyName ?? p?.name ?? "";
    const sel = normalizePartyName(name);
    if (!sel) continue;
    if (rowParty === sel) return true;
  }
  return false;
}

function emptyByBrand() {
  return Object.fromEntries(MONTHLY_AUTOS_BRANDS.map((b) => [b, { oe: 0, ord: 0 }]));
}

/** Classify one import sale line → brand + OE vs ordinary amount. */
function classifySaleLine(row) {
  const amt = Math.abs(lineAmountOnly(row));
  if (!(amt > 0)) return null;

  const brandName = importRowBrandNameField(row);
  if (brandName) {
    const oeB = oeBrandFromBrandField(brandName);
    if (oeB) return { brand: oeB, oe: amt, ord: 0 };
    const b = normalizeBrand(brandName);
    if (MONTHLY_AUTOS_BRANDS.includes(b)) return { brand: b, oe: 0, ord: amt };
  }

  const group = String(row.itemGroup ?? row.itemgroup ?? row["Item Group"] ?? "").trim();
  if (group) {
    const oeB = oeBrandFromBrandField(group);
    if (oeB) return { brand: oeB, oe: amt, ord: 0 };
    const b = normalizeBrand(group);
    if (MONTHLY_AUTOS_BRANDS.includes(b)) return { brand: b, oe: 0, ord: amt };
  }

  return null;
}

/** Load sale voucher lines for one calendar month from `sales_import_months`. */
export async function loadBranchSaleRowsForMonth(year, monthIndex, branchFilter) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const { start, end } = monthWindow(y, mi);

  let rowFilter = null;
  let rowFilterKey = "all";
  if (branchFilter === "Main") {
    rowFilter = (row) => includeRowInMonthlyImportReport(row, "Main");
    rowFilterKey = "main-full";
  }

  const rows = await loadSalesImportRows({
    fromDate: ymdLocal(start),
    toDate: ymdLocal(end),
    rowFilter,
    rowFilterKey,
  });

  const branchRows =
    branchFilter === "Main"
      ? rows
      : (rows || []).filter((row) => appBranchFromImportRow(row) === branchFilter);

  return (branchRows || []).filter((row) => isImportSaleVoucherRow(row));
}

/**
 * Distinct customer names on sale lines in `sales_import_months` for this month + branch.
 */
export async function fetchImportCustomerNamesForIncentive({
  year,
  monthIndex,
  branchFilter = "Main",
  search = "",
}) {
  const saleRows = await loadBranchSaleRowsForMonth(year, monthIndex, branchFilter);
  const names = new Set();
  for (const row of saleRows) {
    const name = importRowPartyName(row);
    if (name) names.add(name);
  }

  let list = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const term = String(search || "").trim().toLowerCase();
  if (term) {
    list = list.filter((n) => n.toLowerCase().includes(term));
  }
  return list;
}

const partyDeductionCache = new Map();

function partyDeductionCacheKey(year, monthIndex, branchFilter, parties) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const sorted = [...parties].map((p) => p.toLowerCase()).sort().join("|");
  return `${y}-${mi}-${branchFilter}::${sorted}`;
}

/**
 * Sum OE & ordinary sale lines for selected parties (current calendar month).
 */
export async function fetchPartyDeductionsForIncentive({
  year,
  monthIndex,
  branchFilter = "Main",
  partyNames = [],
}) {
  const parties = (partyNames || [])
    .map((p) => (typeof p === "string" ? p : p?.partyName ?? p?.name ?? ""))
    .map((s) => String(s).trim())
    .filter(Boolean);

  const byBrand = emptyByBrand();
  if (!parties.length) {
    return { byBrand, totalOe: 0, totalOrd: 0, parties: [] };
  }

  const cacheKey = partyDeductionCacheKey(year, monthIndex, branchFilter, parties);
  if (partyDeductionCache.has(cacheKey)) {
    return partyDeductionCache.get(cacheKey);
  }

  const saleRows = await loadBranchSaleRowsForMonth(year, monthIndex, branchFilter);

  let totalOe = 0;
  let totalOrd = 0;

  for (const row of saleRows) {
    if (!rowMatchesSelectedParties(row, parties)) continue;

    const cls = classifySaleLine(row);
    if (!cls?.brand || !byBrand[cls.brand]) continue;

    byBrand[cls.brand].oe += cls.oe;
    byBrand[cls.brand].ord += cls.ord;
    totalOe += cls.oe;
    totalOrd += cls.ord;
  }

  const result = { byBrand, totalOe, totalOrd, parties };
  partyDeductionCache.set(cacheKey, result);
  return result;
}

/**
 * Sum OE & ordinary sale lines tagged with Employee Name on import rows (current calendar month).
 */
export async function fetchEmployeeTaggedSalesOeOrdinary({
  year,
  monthIndex,
  branchFilter = "Main",
  employeeNames = [],
}) {
  const names = (employeeNames || [])
    .map((n) => (typeof n === "string" ? n : n?.name ?? ""))
    .map((s) => String(s).trim())
    .filter(Boolean);

  const byBrand = emptyByBrand();
  if (!names.length) {
    return { byBrand, totalOe: 0, totalOrd: 0, employeeNames: [] };
  }

  const saleRows = await loadBranchSaleRowsForMonth(year, monthIndex, branchFilter);

  let totalOe = 0;
  let totalOrd = 0;

  for (const row of saleRows) {
    if (!rowMatchesSelectedEmployees(row, names)) continue;

    const cls = classifySaleLine(row);
    if (!cls?.brand || !byBrand[cls.brand]) continue;

    byBrand[cls.brand].oe += cls.oe;
    byBrand[cls.brand].ord += cls.ord;
    totalOe += cls.oe;
    totalOrd += cls.ord;
  }

  return { byBrand, totalOe, totalOrd, employeeNames: names };
}
